import React, { useState, useContext, useEffect } from "react";
import companyBasedRequest from "../../../request/companyBasedRequest";
import translations from "../../../translations/translations";
import "./shopHoursModal.css";
import { CombinedContext } from "../../../context/combinedContext";

const DEFAULT_HOURS = [
	{ dayEN: "Monday", dayES: "Lunes", open: "09:00", close: "17:00", closed: false },
	{ dayEN: "Tuesday", dayES: "Martes", open: "09:00", close: "17:00", closed: false },
	{ dayEN: "Wednesday", dayES: "Miércoles", open: "09:00", close: "17:00", closed: false },
	{ dayEN: "Thursday", dayES: "Jueves", open: "09:00", close: "17:00", closed: false },
	{ dayEN: "Friday", dayES: "Viernes", open: "09:00", close: "17:00", closed: false },
	{ dayEN: "Saturday", dayES: "Sábado", open: "09:00", close: "17:00", closed: false },
	{ dayEN: "Sunday", dayES: "Domingo", open: "09:00", close: "17:00", closed: false },
];
const ShopHoursModal = ({ open, onClose, shopData, setShopData }) => {
	const { language, setUserData, userData} = useContext(CombinedContext);

	const [localHours, setLocalHours] = useState([]);
	const [saving, setSaving] = useState(false);

	// ✅ Load shop hours into local state

	useEffect(() => {
		if (shopData?.openingHours?.length > 0) {
			setLocalHours([...shopData.openingHours]);
		} else {
			setLocalHours([...DEFAULT_HOURS]);
		}
	}, [shopData]);

	if (!open) return null;

	const handleHourChange = (index, field, value) => {
		setLocalHours((prev) => {
			const updated = [...prev];
			const day = { ...updated[index] };
	
			const toMinutes = (time) => {
				if (!time) return null;
				const [h, m] = time.split(":").map(Number);
				return h * 60 + m;
			};
	
			const newOpen = field === "open" ? value : day.open;
			const newClose = field === "close" ? value : day.close;
	
			if (!day.closed && newOpen && newClose) {
				const openMins = toMinutes(newOpen);
				const closeMins = toMinutes(newClose);
	
				// ❌ reject invalid change
				if (openMins >= closeMins) {
					return prev;
				}
			}
	
			// ✅ accept change
			day[field] = value;
			updated[index] = day;
			return updated;
		});
	};
	

	const toggleClosed = (index) => {
		const updated = [...localHours];
		updated[index].closed = !updated[index].closed;

		// 👇 Auto-clear hours if closed
		if (updated[index].closed) {
			updated[index].open = "00:00";
			updated[index].close = "00:00";
		}
		setLocalHours(updated);
	};

	const handleSave = async () => {
		try {
			setSaving(true)
			const res = await companyBasedRequest.updateShopHours(
				shopData.shopId,
				{ openingHours: localHours },
				userData.cognitoTokens.AccessToken
			);
	
			setShopData((prev) => {
				const hasSteps =
					prev?.setup &&
					prev?.setup.steps &&
					typeof prev.setup.steps === "object";
	
				return {
					...prev,
					openingHours: localHours,
	
					// ✅ only update steps if they exist
					...(hasSteps && {
						setup: {
							...prev.setup,
							steps: {
								...prev.setup.steps,
								hours: true,
							},
						},
					}),
				};
			});
	
			// ✅ backend is source of truth
			setUserData((prev) => ({
				...prev,
				shop: res.shop,
			}));
	
			onClose();
		} catch (err) {
			console.error("Failed to update hours:", err);
		} finally {
			setSaving(false)
		}
	};

	const times = Array.from({ length: 48 }, (_, i) => {
        const h = Math.floor(i / 2);
        const m = i % 2 === 0 ? "00" : "30";
        return `${String(h).padStart(2, "0")}:${m}`;
    });

	function formatTimeDisplay(time) {
		if (!time) return "";
	
		const [h, m] = time.split(":").map(Number);
		const hour12 = h % 12 || 12;
		const minutes = String(m).padStart(2, "0");
		const period = h >= 12 ? "PM" : "AM";
	
		return `${hour12}:${minutes} ${period}`;
	}


	return (
		<div className="sf-book-overlay" onClick={onClose}>
			<div className="sf-book-modal" onClick={(e) => e.stopPropagation()}>
				<div className="sf-profile-modal-header">
					<h2 className="sf-profile-modal-title">{translations[language].profile.sidebar.hours.title}</h2>
					<button className="sf-book-close" onClick={onClose}>
						✕
					</button>
				</div>

				<div className="sf-book-body">
					{localHours.map((day, index) => (
						<div className="sf-hours-row" key={index}>
							<div className="sf-hours-day">{language === "es" ? day.dayES : day.dayEN}</div>

							{!day.closed ? (
								<div className="sf-hours-inputs">
								<select
									value={day.open}
									onChange={(e) => handleHourChange(index, "open", e.target.value)}
								>
									{times.map((t) => (
										<option key={t} value={t}>
											{formatTimeDisplay(t)}
										</option>
									))}
								</select>

								<span> - </span>

								<select
									value={day.close}
									onChange={(e) => handleHourChange(index, "close", e.target.value)}
								>
									{times
										.filter((t) => t > day.open)
										.map((t) => (
											<option key={t} value={t}>
												{formatTimeDisplay(t)}
											</option>
										))}
								</select>
							</div>
							) : (
								<div className="sf-hours-closed">
									{translations[language].profile.sidebar.hours.closed}
								</div>
							)}


							<button className="sf-hours-toggle" onClick={() => toggleClosed(index)}>
								{day.closed ? (language === "es" ? "Marcar abierto" : "Mark Open") : language === "es" ? "Marcar cerrado" : "Mark Closed"}
							</button>
						</div>
					))}
				</div>

				<div className="sf-base-footer">
					<button className="btn ghost" onClick={onClose}>
						{translations[language].calendar.editor.cancel}
					</button>
					<button className="btn" onClick={handleSave} disabled={saving}>
						{saving ? translations[language].calendar.editor.saving : translations[language].calendar.editor.save}
					</button>
				</div>
			</div>
		</div>
	);
};

export default ShopHoursModal;
